import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import {
  AdminPortalLogin,
  ClientPortalLogin,
  OperatorPortalLogin,
  OwnerPortalLogin,
} from './pages/Login';
import RegisterPage, { ClientRegisterPage, OwnerRegisterPage } from './pages/RegisterPage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AdminDashboard from './pages/AdminDashboard';
import OwnerDashboard from './pages/OwnerDashboard';
import ClientDashboard from './pages/ClientDashboard';
import OperatorDashboard from './pages/OperatorDashboard';
import PremiumUI from './premium-ui/PremiumUI';
import LegalPolicyPage from './pages/legal/LegalPolicyPage';
import CancellationAndRefundsPage from './pages/legal/CancellationAndRefundsPage';
import DeleteAccountPage from './pages/legal/DeleteAccountPage';
import SurveyForm from './components/SurveyForm';
import SurveyLanding from './pages/SurveyLanding';
import SurveyOwnerType from './pages/SurveyOwnerType';
import SurveyClientProfession from './pages/SurveyClientProfession';
import FreelancerApplyPage from './pages/FreelancerApplyPage';
import { onForegroundMessage, requestPushPermission } from './services/pushNotifications';
import { getRoleLoginPath } from './utils/authLogout';
import { secureFetch } from './supabaseService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';
const isDevEnvironment = process.env.NODE_ENV !== 'production';
let getDevRoutes = null;
if (isDevEnvironment) {
  // Loaded only in development so production bundles omit dev preview pages.
  // eslint-disable-next-line global-require
  getDevRoutes = require('./routes/DevOnlyRoutes').getDevRoutes;
}

function ProtectedRoute({ role, children, authReady, user }) {
  const token = localStorage.getItem('developmentexpress_token');
  // Prefer latest localStorage user so post-login role switches are immediate.
  // `sessionUser` can be stale from a previous session until `/me` refresh completes.
  let storedUser = null;
  if (!storedUser) {
    try {
      storedUser = JSON.parse(localStorage.getItem('developmentexpress_user') || 'null');
    } catch (_err) {
      storedUser = null;
    }
  }
  if (!storedUser && user) {
    storedUser = user;
  }
  if (!authReady) {
    return <div style={{ color: '#c9a84c', textAlign: 'center', paddingTop: '20vh' }}>Validating session...</div>;
  }
  if (!token || !storedUser?.role) {
    return <Navigate to={getRoleLoginPath(storedUser?.role || role)} replace />;
  }
  const roleAllowed = !role
    || storedUser.role === role
    || (role === 'admin' && storedUser.role === 'manager');
  if (!roleAllowed) {
    return <Navigate to={`/${storedUser.role}`} replace />;
  }
  return children;
}

function ExternalRedirect({ to }) {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
}

function App() {
  const [authReady, setAuthReady] = useState(false);
  const [sessionUser, setSessionUser] = useState(null);

  useEffect(() => {
    const validateSession = async () => {
      try {
        const token = localStorage.getItem('developmentexpress_token');
        if (!token) {
          setAuthReady(true);
          return;
        }

        const payload = await secureFetch('/api/auth/me');
        if (payload?.user) {
          const { syncUserProfilePhoto } = await import('./utils/profilePhotoStorage');
          const user = syncUserProfilePhoto(payload.user);
          localStorage.setItem('developmentexpress_user', JSON.stringify(user));
          setSessionUser(user);
          if (user.pushNotificationsEnabled) {
            requestPushPermission(localStorage.getItem('developmentexpress_token'));
          }
        }
      } catch (err) {
        if ((err?.message || '') === 'auth_required') {
          setSessionUser(null);
          return;
        }
        localStorage.removeItem('developmentexpress_user');
        localStorage.removeItem('developmentexpress_token');
        localStorage.removeItem('developmentexpress_refresh_token');
        setSessionUser(null);
      } finally {
        setAuthReady(true);
      }
    };

    validateSession();
  }, []);

  useEffect(() => {
    const refreshToken = () => {
      const token = localStorage.getItem(
        'developmentexpress_token',
      );
      const refresh = localStorage.getItem(
        'developmentexpress_refresh_token',
      );
      if (!token || !refresh) return;

      fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refreshToken: refresh,
        }),
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data?.token) {
            localStorage.setItem(
              'developmentexpress_token',
              data.token,
            );
            if (data.refreshToken) {
              localStorage.setItem(
                'developmentexpress_refresh_token',
                data.refreshToken,
              );
            }
          }
        })
        .catch(() => {
          // Non-critical — silent fail
        });
    };

    // Refresh every 25 minutes (token expires at 30 min)
    const interval = setInterval(
      refreshToken,
      25 * 60 * 1000,
    );

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const unsubscribe = onForegroundMessage((payload) => {
      const { title, body } = payload.notification || {};
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        new Notification(title || 'MachineOS', { body: body || '', icon: '/logo192.png' });
      }
    });
    return unsubscribe;
  }, []);

  return (
    <LanguageProvider>
      <Router>
        <Routes>
          <Route path="/" element={<ExternalRedirect to="https://developmentexpress.in" />} />
          <Route path="/login" element={<ExternalRedirect to="https://developmentexpress.in" />} />
          <Route path="/login/client" element={<ClientPortalLogin />} />
          <Route path="/login/owner" element={<OwnerPortalLogin />} />
          <Route path="/login/operator" element={<OperatorPortalLogin />} />
          <Route path="/login/admin" element={<AdminPortalLogin />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/register/client" element={<ClientRegisterPage />} />
          <Route path="/register/owner" element={<OwnerRegisterPage />} />
          {getDevRoutes ? getDevRoutes() : null}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/terms" element={<LegalPolicyPage />} />
          <Route path="/privacy" element={<LegalPolicyPage />} />
          <Route path="/delete-account" element={<DeleteAccountPage />} />
          <Route path="/refund-policy" element={<LegalPolicyPage />} />
          <Route path="/cancellation-policy" element={<LegalPolicyPage />} />
          <Route path="/cancellation-and-refunds" element={<CancellationAndRefundsPage />} />
          <Route path="/service-delivery-policy" element={<LegalPolicyPage />} />
          <Route path="/shipping-policy" element={<Navigate to="/service-delivery-policy" replace />} />
          <Route path="/contact" element={<LegalPolicyPage />} />
          <Route path="/pricing" element={<LegalPolicyPage />} />
          <Route path="/grievance" element={<LegalPolicyPage slug="grievance" />} />
          <Route path="/premium" element={<PremiumUI />} />
          <Route path="/survey" element={<SurveyLanding />} />
          <Route path="/survey/owner/type" element={<SurveyOwnerType />} />
          <Route path="/survey/client/profession" element={<SurveyClientProfession />} />
          <Route path="/survey/:type" element={<SurveyForm />} />
          <Route path="/freelancer/apply" element={<FreelancerApplyPage />} />
          <Route path="/admin" element={<ProtectedRoute role="admin" authReady={authReady} user={sessionUser}><AdminDashboard /></ProtectedRoute>} />
          <Route path="/owner" element={<ProtectedRoute role="owner" authReady={authReady} user={sessionUser}><OwnerDashboard /></ProtectedRoute>} />
          <Route path="/client" element={<ProtectedRoute role="client" authReady={authReady} user={sessionUser}><ClientDashboard /></ProtectedRoute>} />
          <Route path="/operator" element={<ProtectedRoute role="operator" authReady={authReady} user={sessionUser}><OperatorDashboard /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    </LanguageProvider>
  );
}

export default App;